import { useMemo, useRef, useState } from 'react';
import {
  Activity, BedDouble, Check, ChevronLeft, ChevronRight, Clock, Gauge, MessageSquare, X, Zap,
} from 'lucide-react';
import { todayKey } from './state';
import { countCompletedSets, sumDayVolume } from './workout';
import { mondayOf, planDayFor } from './logic/schedule';
import { FONT_MONO } from './theme';
import { Card, StatTile } from './ui';
import { CardTitle, extraColors } from './stats/common';
import { dayKey, dayNum } from './stats/statsData';
import { TrainingHeatmap, HeatmapLegend } from './history/TrainingHeatmap';
import {
  dayExercises, fmtDuration, heatmapColumns, latestSessionDate, missedTally, rirLabel, sessionInfo,
} from './history/heatmapData';

/* ────────────────────────────────────────────────────────────────────────── */
/*  History tab                                                               */
/* ────────────────────────────────────────────────────────────────────────── */

const WEEKS_STEP = 24;

export function HistoryTab({ t, state, isDark = true }) {
  const today = todayKey();
  const [weeks, setWeeks] = useState(WEEKS_STEP);
  // Open on the last session: that is what History is usually opened for.
  const [selectedKey, setSelectedKey] = useState(() => latestSessionDate(state.logs, todayKey()));
  const detailRef = useRef(null);
  const c = extraColors(isDark);
  const colors = { amber: c.amber, tick: c.gold };

  const { logs, plan, sessions, cycleAnchor } = state;
  const heat = useMemo(
    () => heatmapColumns({ logs, plan, sessions, cycleAnchor }, today, { weeks }),
    [logs, plan, sessions, cycleAnchor, today, weeks]
  );
  const summary = useMemo(() => {
    let trained = 0;
    let missed = 0;
    heat.columns.forEach(col => col.days.forEach(d => {
      if (d.kind === 'trained' || d.kind === 'extra') trained += 1;
      if (d.kind === 'missed') missed += 1;
    }));
    return { trained, missed, tally: missedTally(heat.columns) };
  }, [heat.columns]);

  const select = (key) => {
    setSelectedKey(key);
    // The detail sits below the calendar, often below the fold on a phone.
    window.requestAnimationFrame(() => {
      const el = detailRef.current;
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card t={t} padding={16}>
        <CardTitle t={t} sub={
          <span>
            Last {heat.columns.length} weeks · {summary.trained} session{summary.trained === 1 ? '' : 's'}
            {summary.missed > 0 && <> · <span style={{ color: c.amber }}>{summary.missed} missed</span></>}
          </span>
        }>Training calendar</CardTitle>
        <TrainingHeatmap t={t} columns={heat.columns} selected={selectedKey} onSelect={select} colors={colors}
          onOlder={heat.hasOlder ? () => setWeeks(w => w + WEEKS_STEP) : null} />
        <HeatmapLegend t={t} colors={colors} />
        {!heat.firstDate && (
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 10, lineHeight: 1.5 }}>
            Sessions show up here once you tick your first set on Today.
          </div>
        )}
        {summary.tally.length > 0 && (
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 10, lineHeight: 1.5 }}>
            <span style={{ color: c.amber, fontWeight: 600 }}>Missed most: </span>
            {summary.tally.slice(0, 3).map(r => `${r.planDay} ${r.missed} of ${r.planned}`).join(' · ')}
          </div>
        )}
      </Card>

      {selectedKey && (
        // scrollMarginTop keeps the card clear of the sticky header.
        <div ref={detailRef} style={{ scrollMarginTop: 110, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <DayDetail t={t} c={c} state={state} date={selectedKey} today={today}
            firstDate={heat.firstDate} min={heat.start} max={heat.end}
            onNavigate={setSelectedKey} onClose={() => setSelectedKey(null)} />
        </div>
      )}
    </div>
  );
}

function NavButton({ t, onClick, disabled, label, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} style={{
      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
      background: 'transparent', border: `1px solid ${t.cardBorder}`, color: t.textMuted,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{children}</button>
  );
}

function Chip({ t, icon: Icon, children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
      color: t.text, background: t.surfaceAlt, border: `1px solid ${t.cardBorder}`,
      borderRadius: 999, padding: '4px 10px', whiteSpace: 'nowrap',
    }}>
      <Icon size={13} color={t.textMuted} />
      {children}
    </span>
  );
}

function Tag({ color, bg, children }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      color, background: bg, borderRadius: 999, padding: '3px 8px',
    }}>{children}</span>
  );
}

function DayDetail({ t, c, state, date, today, firstDate, min, max, onNavigate, onClose }) {
  const n = dayNum(date);
  const selDate = new Date(`${date}T12:00:00`);
  // The stamped plan day first, so a later cycle shift never relabels it.
  const p = planDayFor(state, date, mondayOf(today));
  const planDay = p ? p.day : null;
  const dayLog = state.logs?.[date] || {};
  const info = sessionInfo(state, date);
  const exercises = dayExercises(state, date, planDay);
  const totalPlanned = exercises.reduce((s, e) => s + (e.planned ? e.sets || 0 : 0), 0);
  const completedSets = countCompletedSets(dayLog);
  const totalVolume = sumDayVolume(dayLog);
  const progress = totalPlanned ? Math.min(100, Math.round((completedSets / totalPlanned) * 100)) : 0;
  const hasAny = completedSets > 0;
  const isFuture = date > today;
  const isRest = !!planDay?.isRest;
  const missed = !hasAny && !isRest && !!planDay && date < today && !!firstDate && date >= firstDate;
  // 0% on a missed day only repeats the Missed tag.
  const showProgress = !isRest && totalPlanned > 0 && !isFuture && (hasAny || date === today);

  return (
    <>
      <Card t={t} padding={16}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <NavButton t={t} label="Previous day" disabled={!!min && date <= min} onClick={() => onNavigate(dayKey(n - 1))}>
            <ChevronLeft size={18} />
          </NavButton>
          <div style={{
            flex: 1, minWidth: 0, textAlign: 'center', fontSize: 11, color: t.textFaint, fontWeight: 600,
            letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.3,
          }}>
            {selDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
          <NavButton t={t} label="Next day" disabled={!!max && date >= max} onClick={() => onNavigate(dayKey(n + 1))}>
            <ChevronRight size={18} />
          </NavButton>
          <NavButton t={t} label="Close day" onClick={onClose}><X size={16} /></NavButton>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: t.text, lineHeight: 1.15 }}>
              {planDay?.name || '—'}
            </div>
            {planDay?.focus && (
              <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4 }}>{planDay.focus}</div>
            )}
            {(missed || (hasAny && isRest) || (info.override && !isRest) || isFuture) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {missed && <Tag color={c.amber} bg={c.amberSoft}>Missed</Tag>}
                {hasAny && isRest && <Tag color={c.gold} bg={c.goldSoft}>Extra on a rest day</Tag>}
                {info.override && !isRest && <Tag color={t.textMuted} bg={t.surfaceAlt}>Chosen day</Tag>}
                {isFuture && <Tag color={t.textMuted} bg={t.surfaceAlt}>Coming up</Tag>}
              </div>
            )}
          </div>
          {showProgress && (
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em' }}>COMPLETED</div>
              <div style={{ fontSize: 20, fontFamily: FONT_MONO, fontWeight: 700, color: progress === 100 ? t.accent : t.text }}>
                {progress}%
              </div>
              <div style={{ fontSize: 11, color: t.textMuted }}>{completedSets}/{totalPlanned} sets</div>
            </div>
          )}
        </div>
        {showProgress && (
          <div style={{ marginTop: 14, height: 4, background: t.surfaceAlt, borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: t.accent }} />
          </div>
        )}

        {/* The session record and the morning check-in, when there is one. */}
        {info.any && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {info.durationMin !== null && <Chip t={t} icon={Clock}>{fmtDuration(info.durationMin)}</Chip>}
              {info.rpe !== null && <Chip t={t} icon={Gauge}>RPE {info.rpe}</Chip>}
              {info.sleepH !== null && <Chip t={t} icon={BedDouble}>{info.sleepH}h sleep</Chip>}
              {info.energy !== null && <Chip t={t} icon={Zap}>Energy {info.energy}/5</Chip>}
              {info.soreness !== null && <Chip t={t} icon={Activity}>Soreness {info.soreness}/5</Chip>}
            </div>
            {info.soreAreas.length > 0 && (
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 8 }}>Sore: {info.soreAreas.join(', ')}</div>
            )}
            {info.note && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, fontSize: 13, color: t.text, lineHeight: 1.45 }}>
                <MessageSquare size={14} color={t.textMuted} style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ overflowWrap: 'anywhere' }}>{info.note}</span>
              </div>
            )}
          </div>
        )}
      </Card>

      {hasAny ? (
        <>
          {exercises
            .map(ex => ({ ex, done: (dayLog[ex.name] || []).filter(s => s?.done) }))
            .filter(({ done }) => done.length > 0)
            .map(({ ex, done }) => <ExerciseCard key={ex.id} t={t} ex={ex} done={done} />)}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <StatTile t={t} label="Total Volume" value={`${totalVolume.toLocaleString()} kg`} />
            <StatTile t={t} label="Sets Done" value={`${completedSets}${totalPlanned ? `/${totalPlanned}` : ''}`} accent={totalPlanned > 0 && completedSets >= totalPlanned} />
          </div>
        </>
      ) : isRest ? (
        <Card t={t} padding={32} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🌙</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>Rest Day</div>
        </Card>
      ) : (
        <Card t={t} padding={28} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.5 }}>
            {isFuture ? 'Planned'
              : date === today ? 'Nothing logged yet today'
                : missed ? 'Nothing logged. On the plan:' : 'No completed sets logged this day'}
          </div>
          {/* What was (or is) on the plan, so a missed day says what got skipped. */}
          {(isFuture || missed) && exercises.length > 0 && (
            <div style={{ fontSize: 13, color: t.text, lineHeight: 1.6, marginTop: 6 }}>
              {exercises.map(e => e.name).join(' · ')}
            </div>
          )}
        </Card>
      )}
    </>
  );
}

function ExerciseCard({ t, ex, done }) {
  const targetSets = ex.sets || Math.max(done.length, 1);
  const completedHere = done.length;
  const exDone = ex.planned ? completedHere >= targetSets : completedHere > 0;
  const topW = done.reduce((m, s) => Math.max(m, Number(s.weight) || 0), 0);
  const exVol = done.reduce((s, x) => s + (Number(x.weight) || 0) * (Number(x.reps) || 0), 0);
  // The last column shows RIR once any set of the exercise has one; a
  // History row is always done, so the tick alone says nothing new there.
  const hasRir = done.some(s => rirLabel(s.rir) !== null);

  return (
    <Card t={t} padding={0} style={{ overflow: 'hidden' }}>
      <div className="fit-hist-card-head" style={{
        padding: '14px 20px 12px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: exDone ? t.accent : t.cardBorder, flexShrink: 0,
          }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.text, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.name}</span>
              {!ex.planned && (
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '1px 6px', borderRadius: 4, background: t.accentSoft, color: t.accent, flexShrink: 0 }}>EXTRA</span>
              )}
            </div>
            {ex.swappedFrom && (
              <div style={{ fontSize: 11, color: t.textFaint, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                instead of {ex.swappedFrom}
              </div>
            )}
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2, fontFamily: FONT_MONO }}>
              {ex.planned ? `target ${targetSets} × ${ex.reps}` : 'Ad-hoc'}
              {`  ·  top ${topW}kg  ·  ${exVol.toLocaleString()}kg vol`}
            </div>
          </div>
        </div>
        <span style={{ fontSize: 12, fontFamily: FONT_MONO, fontWeight: 600, color: exDone ? t.accent : t.textMuted, flexShrink: 0 }}>
          {ex.planned ? `${completedHere}/${targetSets}` : `${completedHere} sets`}
        </span>
      </div>

      <div className="fit-hist-card-body" style={{ padding: '0 20px 14px', borderTop: `1px solid ${t.divider}` }}>
        <div className="fit-set-grid-history" style={{ display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr) minmax(0, 1fr) 60px', gap: 8, padding: '10px 0 6px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.textFaint }}>
          <span style={{ textAlign: 'center' }}>Set</span>
          <span style={{ textAlign: 'center' }}>kg</span>
          <span style={{ textAlign: 'center' }}>Reps</span>
          <span style={{ textAlign: 'center' }}>{hasRir ? 'RIR' : 'Done'}</span>
        </div>
        {done.map((sd, i) => {
          const w = Number(sd.weight) || 0;
          const r = Number(sd.reps) || 0;
          const rir = rirLabel(sd.rir);
          return (
            <div key={i} className="fit-set-grid-history" style={{
              display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr) minmax(0, 1fr) 60px', gap: 8,
              padding: '8px 0', alignItems: 'center',
              borderTop: i > 0 ? `1px solid ${t.divider}` : 'none',
            }}>
              <span style={{ textAlign: 'center', fontSize: 12, fontFamily: FONT_MONO, fontWeight: 700, color: t.accent }}>
                {i + 1}
              </span>
              <span style={{ textAlign: 'center', fontSize: 14, fontFamily: FONT_MONO, fontWeight: 600, color: t.text }}>
                {w > 0 ? w : '—'}
              </span>
              <span style={{ textAlign: 'center', fontSize: 14, fontFamily: FONT_MONO, fontWeight: 600, color: t.text }}>
                {r > 0 ? r : '—'}
              </span>
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                {!hasRir ? (
                  <span style={{
                    width: 24, height: 24, borderRadius: 6,
                    background: t.accent, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><Check size={14} /></span>
                ) : rir !== null ? (
                  <span title={`${rir} reps in reserve`} style={{
                    minWidth: 30, height: 22, borderRadius: 6, padding: '0 6px',
                    background: t.accentSoft, color: t.accent, border: `1px solid ${t.accentBorder}`,
                    fontSize: 12, fontFamily: FONT_MONO, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{rir}</span>
                ) : (
                  <span style={{ fontSize: 12, color: t.textFaint }}>–</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
